import { BaseComponent } from '../base-component'
import { getFocusTargets, $el, $list } from '../../utils/utils'
import { isTabKey } from '../../utils/key'
import { safePolygon } from './safe-polygon'

export class FloatElementBtn extends BaseComponent {
  element: any
  safePolygonFn: any

  constructor() {
    super()
    this.element = this.getElement()
  }

  mountComponent(): void {
    this.addListener(this.element, 'keyup', this.handleKeyUp)
    this.addListener(this.element, 'blur', this.handleBlur)

    if (this.dataset.trigger === 'click') {
      this.addListener(window, 'click', this.handleWindowClick)
      this.addListener(this.element, 'click', this.handleElementClick)
    } else {
      this.addListener(this.element, 'mouseleave', this.handleMouseLeave)
      this.addListener(this.element, 'mouseenter', this.handleMouseEnter)
    }
  }

  handleBlur = (): void => {
    if (!this.floatElement) {
      return
    }
  }

  handleKeyUp = (evt: KeyboardEvent): void => {
    if (!isTabKey(evt)) {
      return
    }

    this.floatElement.show()
    this.setTargetListeners('add')
  }

  handleFirstTargetFocus = (): void => {}

  handleTargetFocus = (): void => {}

  handleLastTargetBlur = (): void => {
    this.setTargetListeners('remove')
    this.floatElement.hide()
  }

  handleMouseEnter = (): void => {
    this.trigger()
  }

  handleElementClick = (): void => {
    this.trigger()
  }

  handleWindowClick = (event: MouseEvent): void => {
    const isCursorOutside = this.floatElement.checkCursorOutside(
      event.clientX,
      event.clientY,
    )
    const isOutsideFloatElements = this.isTargetOutsideFloatElements(
      event.target,
    )

    if (isCursorOutside && isOutsideFloatElements && this.floatElement.isOpen) {
      this.removeListener(window, 'click', this.handleWindowClick)
      this.floatElement.hide()
    }
  }

  handleMouseLeave = (event: MouseEvent): void => {
    if (this.safePolygonFn) {
      this.removeListener(window, 'mousemove', this.handleMouseMove)
    }

    this.safePolygonFn = safePolygon()({
      x: event.clientX,
      y: event.clientY,
      placement: this.floatElement.dataset.placement,
      onClose: () => {
        this.removeListener(window, 'mousemove', this.handleMouseMove)

        if (this.floatElement && this.floatElement.isOpen) {
          this.floatElement.hide()
        }
      },
      elements: { domReference: this.element, floating: this.floatElement },
      nodeId: null,
      tree: null,
    })

    this.safePolygonFn(event)

    this.addListener(window, 'mousemove', this.handleMouseMove)
  }

  handleMouseMove = (event: MouseEvent): void => {
    this.safePolygonFn(event)
  }

  setTargetListeners(action): void {
    const { targets, firstTarget, lastTarget } = this.getTargets()
    const listenerSetter = action === 'add' ? 'addListener' : 'removeListener'

    this[listenerSetter](firstTarget, 'focus', this.handleFirstTargetFocus)
    targets.forEach(target => {
      this[listenerSetter](target, 'focus', this.handleTargetFocus)
    })
    this[listenerSetter](lastTarget, 'blur', this.handleLastTargetBlur)
    this[listenerSetter](window, 'click', this.handleWindowClick)
  }

  getElement() {
    return (
      $el(
        `[data-float-element-trigger="${this.dataset.floatElementId}"]`,
        this,
      ) || this
    )
  }

  getTargets() {
    const element = this.floatElement.element
    const targets = $list(getFocusTargets(), element)
    const firstTarget = targets[0]
    const lastTarget = targets[targets.length - 1]

    return { targets, firstTarget, lastTarget }
  }

  isTargetOutsideFloatElements(target) {
    return !this.floatElement.contains(target) && !this.contains(target)
  }

  trigger = (): void => {
    const triggerAction =
      this.dataset.trigger === 'click' ? 'click' : 'mouseenter'

    if (triggerAction === 'click') {
      this.floatElement.isOpen
        ? this.floatElement.hide()
        : this.floatElement.show()

      this.addListener(window, 'click', this.handleWindowClick)
    } else {
      this.floatElement.show()
      this.addListener(this.element, 'mouseleave', this.handleMouseLeave)
    }
  }

  setExpand(isExpanded): void {
    this.setAttribute('data-aria-expanded', isExpanded)
  }

  get floatElement(): any {
    if (!this.dataset.floatElementId) {
      return
    }

    return $el(`#${this.dataset.floatElementId}`)
  }

  get isOpen(): boolean {
    return this.floatElement.isOpen
  }
}
